import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export function AboutPage() {
  const milestones = [
    {
      year: '2008',
      title: 'Unternehmensgründung',
      description: 'Start als spezialisierter Anbieter für Werbe- und Promotionprodukte in Deutschland.'
    },
    {
      year: '2012',
      title: 'Expansion EU-Markt',
      description: 'Ausweitung der Lieferungen auf den gesamten europäischen Markt.'
    },
    {
      year: '2016',
      title: 'Technologie-Upgrade',
      description: 'Investition in modernste Drucktechnologie für höchste Qualitätsstandards.'
    },
    {
      year: '2020',
      title: 'Nachhaltigkeitsinitiative',
      description: 'Einführung umweltfreundlicher Produktionsverfahren und recycelbarer Materialien.'
    },
    {
      year: '2025',
      title: 'Marktführerschaft',
      description: 'Heute einer der führenden Anbieter für professionelle Promotion-Fahnen in der DACH-Region.'
    }
  ];

  const values = [
    {
      icon: 'ri-award-line',
      title: 'Qualität',
      description: 'Wir setzen ausschließlich auf hochwertige Materialien und modernste Produktionsverfahren für langlebige Ergebnisse.'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Service',
      description: 'Persönliche Beratung und maßgeschneiderte Lösungen stehen im Mittelpunkt unserer Kundenbeziehungen.'
    },
    {
      icon: 'ri-time-line',
      title: 'Zuverlässigkeit',
      description: 'Termingerechte Lieferung und verlässliche Qualität – darauf können sich unsere Kunden verlassen.'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      description: 'Kontinuierliche Weiterentwicklung unserer Produkte und Produktionsverfahren für optimale Ergebnisse.'
    }
  ];

  const schema = {
    '@type': 'AboutPage',
    name: 'Über Horizon Signage',
    description: 'Mehr als 10 Jahre Erfahrung in der Herstellung von Promotion-Fahnen und Werbefahnen. Qualität, Zuverlässigkeit und Beständigkeit seit 2008.',
    mainEntity: {
      '@type': 'Organization',
      name: 'Horizon Signage',
      foundingDate: '2008',
      description: 'Spezialisierter Anbieter für Werbe- und Promotionprodukte in Deutschland'
    }
  };

  return (
    <>
      <SEO
        title="Über uns - Horizon Signage | 10+ Jahre Erfahrung Promotion-Fahnen Deutschland"
        description="Mehr als 10 Jahre Erfahrung in der Herstellung von Promotion-Fahnen und Werbefahnen. Horizon Signage steht für Qualität, Zuverlässigkeit und Beständigkeit. Made in Germany seit 2008."
        keywords="Horizon Signage, Über uns, Unternehmen, Promotion-Fahnen Hersteller, Deutschland, Erfahrung"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/uber-uns`}
        schema={schema}
      />
      <div className="min-h-screen">
        <Header />
        
        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <span className="inline-block px-4 py-2 bg-zinc-800 text-white rounded-full text-sm font-semibold uppercase tracking-wider mb-6">
                    Über uns
                  </span>
                  <h1 className="text-5xl lg:text-6xl font-bold text-zinc-800 leading-tight mb-6">
                    <span className="block">Mehr als 10 Jahre</span>
                    <span className="block text-gray-600">Erfahrung in der Branche</span>
                  </h1>
                  <p className="text-xl text-gray-600 leading-relaxed mb-8">
                    Unser Unternehmen verfügt über mehr als 10 Jahre Erfahrung in der Branche und arbeitet nach den Grundsätzen Qualität, Zuverlässigkeit und Beständigkeit. Für den deutschen und europäischen Markt setzen wir auf Produktionsprozesse gemäß internationalen Qualitätsstandards sowie auf eine strukturierte, transparente und professionelle Arbeitsweise. Termintreue, gleichbleibende Produktqualität und ein verlässlicher Service bilden die Grundlage unserer langfristigen und partnerschaftlichen Zusammenarbeit mit unseren Geschäftspartnern.
                  </p>
                  <div className="grid grid-cols-3 gap-8">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-zinc-800 mb-2">500+</div>
                      <div className="text-sm text-gray-600">Zufriedene Kunden</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-zinc-800 mb-2">10+</div>
                      <div className="text-sm text-gray-600">Jahre Erfahrung</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-zinc-800 mb-2">EU</div>
                      <div className="text-sm text-gray-600">Liefergebiet</div>
                    </div>
                  </div>
                </div>
                
                <div className="relative">
                  <img
                    src="https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/649803e6100c509554e85a53fb59b539.jpeg"
                    alt="Horizon Signage Unternehmenssitz"
                    className="w-full h-96 object-cover rounded-3xl shadow-2xl"
                  />
                  <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-sm">
                    <h3 className="font-bold text-zinc-800 mb-2">Made in Germany</h3>
                    <p className="text-sm text-gray-600">
                      Höchste Qualitätsstandards durch lokale Produktion
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Company Story Timeline */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-zinc-800 mb-6">Unsere Unternehmensgeschichte</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Von der Gründung bis heute – eine Erfolgsgeschichte geprägt von Innovation, 
                  Qualität und Kundenzufriedenheit.
                </p>
              </div>

              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gray-200"></div>

                <div className="space-y-16">
                  {milestones.map((milestone, index) => (
                    <div key={index} className={`flex items-center ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                      <div className={`w-full lg:w-1/2 ${index % 2 === 0 ? 'lg:pr-16' : 'lg:pl-16'}`}>
                        <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                          <div className="text-3xl font-bold text-zinc-800 mb-2">{milestone.year}</div>
                          <h3 className="text-xl font-bold text-zinc-800 mb-4">{milestone.title}</h3>
                          <p className="text-gray-600 leading-relaxed">{milestone.description}</p>
                        </div>
                      </div>
                      
                      {/* Timeline Dot */}
                      <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-zinc-800 rounded-full border-4 border-white shadow-lg"></div>
                      
                      <div className="hidden lg:block w-1/2"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Values Section */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-zinc-800 mb-6">Unsere Werte</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Diese Grundsätze leiten unser tägliches Handeln und prägen unsere 
                  Unternehmenskultur seit der ersten Stunde.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {values.map((value, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-8 text-center hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                  >
                    <div className="w-16 h-16 flex items-center justify-center bg-zinc-800 rounded-2xl mx-auto mb-6">
                      <i className={`${value.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-zinc-800 mb-4">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Team & Production */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <h2 className="text-4xl font-bold text-zinc-800 mb-6">
                    Expertise und modernste Technologie
                  </h2>
                  <div className="space-y-6 text-lg text-gray-600">
                    <p>
                      Unser erfahrenes Team aus Designern, Produktionsspezialisten und 
                      Kundenberatern arbeitet täglich daran, Ihnen die bestmöglichen 
                      Lösungen für Ihre Werbe- und Promotionanforderungen zu bieten.
                    </p>
                    <p>
                      Mit modernster Drucktechnologie und einem strengen Qualitätsmanagement 
                      stellen wir sicher, dass jedes Produkt unsere hohen Standards erfüllt 
                      und Ihre Erwartungen übertrifft.
                    </p>
                    <p>
                      Unsere Produktionsstätte in Deutschland garantiert nicht nur kurze 
                      Lieferzeiten, sondern auch die Einhaltung strenger Umwelt- und 
                      Qualitätsrichtlinien.
                    </p>
                  </div>

                  <div className="mt-10 flex space-x-4">
                    <a
                      href="/kontakt"
                      className="inline-flex items-center justify-center px-8 py-4 bg-zinc-800 text-white font-semibold rounded-lg hover:bg-zinc-900 transition-colors whitespace-nowrap"
                    >
                      Team kennenlernen
                      <i className="ri-arrow-right-line ml-2"></i>
                    </a>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <img
                    src="https://public.readdy.ai/ai/img_res/6a95abaae837bce14824864d5bb61798.jpg"
                    alt="Team bei der Arbeit"
                    className="w-full h-64 object-cover rounded-2xl shadow-lg"
                  />
                  <img
                    src="https://readdy.ai/api/search-image?query=advanced%20printing%20machinery%20and%20technology%20for%20banner%20and%20flag%20production%2C%20modern%20industrial%20equipment%20with%20precision%20printing%20capabilities%2C%20high-tech%20production%20facility%20interior&width=300&height=400&seq=about-tech-001&orientation=portrait"
                    alt="Produktionstechnologie"
                    className="w-full h-64 object-cover rounded-2xl shadow-lg mt-8"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-zinc-800">
            <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
              <h2 className="text-4xl font-bold text-white mb-6">
                Werden Sie Teil unserer Erfolgsgeschichte
              </h2>
              <p className="text-xl text-gray-300 mb-10">
                Lassen Sie uns gemeinsam Ihre nächste Werbekampagne zum Erfolg machen. 
                Kontaktieren Sie uns für ein unverbindliches Beratungsgespräch.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/kontakt"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-zinc-800 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                >
                  Kostenlose Beratung
                  <i className="ri-phone-line ml-2"></i>
                </a>
                <a
                  href="/produkte"
                  className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-zinc-800 transition-colors whitespace-nowrap"
                >
                  Produktkatalog
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
